import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Grid } from '@material-ui/core';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Listings } from 'containers/AppContainer/typings';
import { handleOpenDocument } from 'services/utils';
import {
  CorporealDocument,
  JudgementADDDocumentType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  ExpertReportType,
  IVehiculeExpertise,
  IVictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import ImportImagineDocumentButton from 'containers/CourtManagerLegalProceedings/ImagineDocuments/ImportButton';
import { Container } from 'containers/CourtManagerLegalProceedings/ExpertReport/styled-components';
import { PAGES } from 'routes';
import { pushTo } from 'containers/services/routes-helper';
import {
  checkExpertiseOnValidation,
  getExpertReportPayload,
} from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import messages from 'containers/CourtManagerLegalProceedings/JudgementAdd/messages';
import { translate } from 'containers/services/translate';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import { useFormatExpertReportState } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/hooks';
import { useDisplayActionButtons } from 'containers/CourtManagerLegalProceedings/ExpertReport/hooks';
import { Claim } from 'containers/PreOpeningPage/typings';
import { hasFeature } from 'features/fflip-helpers';
import {
  extractExpertReport,
  getDeactivationButton,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/utils';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import ExpertReportAppeal from 'containers/common/LegalProceedings/ExpertReportAppeal';
import { addEvaluationsToExpertReportAppealPayload } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/utils';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import Document from 'components/Document';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { DOCUMENT_TYPES } from 'constants/global';
import RejectionDialog from 'containers/CourtManagerLegalProceedings/ExpertReport/RejectionDialog';
import JudgementValidationButtons from 'containers/common/LegalProceedings/JudgementValidationButtons';
import {
  EMPTY_SUPPORT_DOC,
  SUPPORT_DOC_EXPERT_REPORT_ID,
} from 'containers/LawyerRecapPage/constants';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import ValidationDialog from 'containers/CourtManagerLegalProceedings/ExpertReport/ValidationDialog';
import { priorityClaimReason } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { resolveSagisClaim } from 'helpers/utils';
import DecisionDropDownSupportDocument from 'containers/common/LegalProceedings/DecisionDropDownSupportDocument';
import {
  STATUS_PENDING,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import { VictimType } from 'typings/globalTypes';

export interface CourtManagerExpertReportAppealProps {
  intl: IntlShape;
  judgementAddDocuments: JudgementADDDocumentType[];
  listings: Listings;
  claimId: number;
  claimNumber: string;
  requestGetExpertiseRaport: Function;
  expertiseReport: ExpertReportType;
  isManager: boolean;
  requestValidateExpertReport: Function;
  lawyer: string;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  claim: Claim;
  victims: VictimType[];
  judiciaryProcessId: number;
  policyInfo: any;
  expertiseRaportFirstInstance: any;
  requestGetExpertiseReportAppeal: Function;
  isAssignedLawyer: boolean;
  documentId?: string;
  setDocumentId?: Function;
  requestCreateExpertiseRaport?: Function;
  requestAddExpertiseSupportDocument?: Function;
  hasJom?: boolean;
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  isConsultation: Boolean;
  requestMakeDecisionSupportDocument?: Function;
  imagineDocuments: any;
  dahirAmount: any;
  requestGetDocByDocumentumId: Function;
}

export const CourtManagerExpertReportAppeal: React.FC<CourtManagerExpertReportAppealProps> = ({
  ...props
}): JSX.Element => {
  const [expertReportState, setExpertReportState] = useFormatExpertReportState(
    props.judgementAddDocuments,
    props.expertiseReport,
  );
  const [deactivateButton, setDeactivateButton] = useState([] as any);

  const expertReport = useMemo(
    () =>
      extractExpertReport(
        expertReportState,
        (v: IVictimExpertise) => !isEmpty(v.victimExpertise),
        (v: IVehiculeExpertise) => !isEmpty(v.vehiculeExpertise),
      ),
    [props.expertiseReport, expertReportState],
  );

  const { victimExpertises, vehiculeExpertises } = expertReport;
  const [isDecisionDialgOpen, setIsDecisionDialgOpen] = useState<boolean>(
    false,
  );

  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState<boolean>(
    false,
  );
  const [supportDocument, setSupportDocument] = useState({
    ...EMPTY_SUPPORT_DOC,
  });

  const [isExpertReportValidated] = [true];

  const shouldDisplayActionButtons = useDisplayActionButtons(
    props.expertiseReport,
  );
  const [openDialogValidation, setOpenDialogValidation] = useState(false);

  useEffect(() => {
    setDeactivateButton(
      getDeactivationButton(victimExpertises, vehiculeExpertises),
    );
  }, [victimExpertises, vehiculeExpertises]);

  const setExpertiseSupportDocument = (document: CorporealDocument) => {
    if (SUPPORT_DOC_EXPERT_REPORT_ID === props.documentId) {
      setSupportDocument(document);
      props.requestAddExpertiseSupportDocument &&
        props.requestAddExpertiseSupportDocument({
          ...document,
          expertReportId: get(props.expertiseReport, 'id'),
          claimId: props.claimId,
          claimNumber: props.claimNumber,
          status: props.isManager ? STATUS_VALIDATED : STATUS_PENDING,
        });
      props.resetUploadedDocument();
    }
  };
  const priorityReasonClaimId = useSelector((state: any) =>
    get(state.toJS(), ['recap', 'claimRecap', 'claim', 'priorityReasonId']),
  );
  const handleValidateExpertReport = () => {
    const expertReportWithStatus = getExpertReportPayload(
      props.expertiseReport,
      expertReport,
    );
    const payload = hasFeature(FEATURES.EXPERT_REPORT_SAGIS_EVALUATION)
      ? addEvaluationsToExpertReportAppealPayload(
          props.expertiseReport,
          props.expertiseRaportFirstInstance,
          expertReportWithStatus,
          props.victims,
          props.claim,
          props.policyInfo,
          props.dahirAmount,
        )
      : expertReportWithStatus;

    props.requestValidateExpertReport(
      {
        ...payload,
        claimNumber: props.claimNumber,
        procedureType: PROCEDURE_TYPES.APPEAL,
        judiciaryProcessId: get(props.expertiseReport, 'judiciaryProcessId'),
        isInboxesActive: featureService.hasFeature(
          FEATURES.ATTRIBUTION_INBOXES_MANAGER,
        ),
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
        isPriorityActive: featureService.hasFeature(FEATURES.PRIORITY_CLAIM),
        priorityClaimReasonId:
          featureService.hasFeature(FEATURES.PRIORITY_CLAIM) &&
          priorityReasonClaimId !== 4
            ? priorityClaimReason(
                get(payload, 'sagisEvaluations', []),
                !isEmpty(
                  get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []),
                )
                  ? get(resolveSagisClaim(props.policyInfo), 'offeredRisks', [])
                      .map((el: any) => Number(el.riskProvisions))
                      .reduce(
                        (accumulator: any, currentValue: any) =>
                          accumulator + currentValue,
                      )
                  : 0,
                props.listings,
                get(expertReport, 'victimExpertises', []),
              )
            : undefined,
      },
      successCallBack,
    );
  };

  const expertiseSupportDocuments = get(
    props.expertiseReport,
    'supportDocuments',
    [],
  ).filter(
    (el: any) =>
      el.type === DOCUMENT_TYPES.SUPPORT_DOCUMENT_APPEAL_EXPERT_REPORT,
  );

  const successCallBack = () => {
    const { lawyer } = props;

    toast.success(
      translate(messages.courtManagerValidationDialogSuccessMessage, props, {
        lawyer,
      }),
    );
    setOpenDialogValidation(false);
    !featureService.hasFeature(FEATURES.EVOLUTIONS_FEE_NOTE) &&
      pushTo(PAGES.COURT_MANAGER_ASSIGNED_CLAIM);
  };

  return (
    <Container id="court-manager-expert-report">
      <ExpertReportAppeal
        listings={props.listings}
        claimId={props.claimId}
        uploadDocument={props.uploadDocument}
        uploadedDocument={props.uploadedDocument}
        resetUploadedDocument={props.resetUploadedDocument}
        expertReportState={expertReportState}
        setExpertReportState={setExpertReportState}
        expertReport={props.expertiseReport}
        setIsDecisionDialgOpen={setIsDecisionDialgOpen}
        readOnly={!isExpertReportValidated}
        isDecisionDialgOpen={isDecisionDialgOpen}
        isManager={
          userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER) &&
          !props.isConsultation
        }
        expertiseRaportFirstInstance={props.expertiseRaportFirstInstance}
        isAssignedLawyer={props.isAssignedLawyer}
        judiciaryProcessId={props.judiciaryProcessId}
        claimNumber={props.claimNumber}
        judgementAddDocuments={props.judgementAddDocuments}
        victimExpertises={victimExpertises}
        vehicleExpertises={vehiculeExpertises}
        setDeactivateButton={setDeactivateButton}
        deactivateButton={deactivateButton}
        documentId={props.documentId}
        setDocumentId={props.setDocumentId}
        requestCreateExpertiseRaport={props.requestCreateExpertiseRaport}
        requestGetExpertiseRaport={props.requestGetExpertiseRaport}
        requestValidateExpertReport={props.requestValidateExpertReport}
        victims={props.victims}
        claim={props.claim}
        policyInfo={props.policyInfo}
        hasJom={props.hasJom}
        sagisVictimInfo={props.sagisVictimInfo}
        sagisOpposingParties={props.sagisOpposingParties}
        isConsultation={props.isConsultation}
        imagineDocuments={props.imagineDocuments}
        dahirAmount={props.dahirAmount}
        requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
      />
      {featureService.hasFeature(FEATURES.ADD_SUPPORT_DOCUMENTS) &&
        !props.isConsultation &&
        !isEmpty(props.expertiseReport) &&
        !userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMONITORINGMANAGER) &&
        featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) &&
        isEmpty(expertiseSupportDocuments) && (
          <section className="add-recap-bloc">
            {props.resetUploadedDocument &&
              props.uploadDocument &&
              props.uploadedDocument && (
                <>
                  <div className="add-recap-bloc-title">
                    {translate(messages.supportDocument, props)}
                  </div>
                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={4}>
                      <UploadDocumentBloc
                        index={1}
                        document={supportDocument}
                        setDocument={setExpertiseSupportDocument}
                        resetUploadedDocument={props.resetUploadedDocument}
                        uploadDocument={props.uploadDocument}
                        uploadedDocument={props.uploadedDocument}
                        docType={
                          DOCUMENT_TYPES.SUPPORT_DOCUMENT_APPEAL_EXPERT_REPORT
                        }
                        setDocumentId={props.setDocumentId}
                        docId={SUPPORT_DOC_EXPERT_REPORT_ID}
                      />
                    </Grid>
                    <Grid item xs={3} style={{ marginTop: '20px' }}>
                      {!isEmpty(props.imagineDocuments) &&
                        props.isManager &&
                        featureService.hasFeature(
                          FEATURES.TRAITEMENT_IMAGINE_DOC,
                        ) && (
                          <ImportImagineDocumentButton
                            listings={props.listings}
                            managerNotifcationsInboxes={props.imagineDocuments}
                            claimId={props.claimId}
                            imagineDocuments={props.imagineDocuments}
                            claimSagisInfo={resolveSagisClaim(props.policyInfo)}
                            uploadDocument={props.uploadDocument}
                            uploadedDocument={props.uploadedDocument}
                            setUploadedDocs={setExpertiseSupportDocument}
                            documentName={
                              DOCUMENT_TYPES.SUPPORT_DOCUMENT_APPEAL_EXPERT_REPORT
                            }
                            documentType={
                              DOCUMENT_TYPES.SUPPORT_DOCUMENT_APPEAL_EXPERT_REPORT
                            }
                            documentId={SUPPORT_DOC_EXPERT_REPORT_ID}
                            setDocumentId={
                              props.setDocumentId
                                ? props.setDocumentId
                                : () => {}
                            }
                            requestGetDocByDocumentumId={
                              props.requestGetDocByDocumentumId
                            }
                          />
                        )}
                    </Grid>
                  </Grid>
                </>
              )}
          </section>
        )}
      {featureService.hasFeature(FEATURES.ADD_SUPPORT_DOCUMENTS) &&
        !isEmpty(expertiseSupportDocuments) && (
          <main>
            <section className="add-recap-bloc">
              <div className="add-recap-bloc-title">
                {translate(messages.supportDocument, props)}
              </div>
              <div className="support-document-info">
                <Document
                  name={get(expertiseSupportDocuments[0], 'name', '')}
                  type={get(expertiseSupportDocuments[0], 'type', '')}
                  icon={
                    <VisibilityIcon
                      className="consult"
                      onClick={handleOpenDocument(
                        expertiseSupportDocuments[0] as any,
                        props.requestGetDocByDocumentumId,
                      )}
                    />
                  }
                />
                {props.isManager && !props.isConsultation && (
                  <DecisionDropDownSupportDocument
                    claimId={props.claimId}
                    claimNumber={props.claimNumber}
                    judiciaryProcessId={props.judiciaryProcessId}
                    supportDocument={expertiseSupportDocuments[0]}
                    requestMakeDecisionSupportDocument={
                      props.requestMakeDecisionSupportDocument
                    }
                    claim={props.claim}
                    procedureType={PROCEDURE_TYPES.APPEAL}
                  />
                )}
              </div>
            </section>
          </main>
        )}
      {shouldDisplayActionButtons &&
        !props.isConsultation &&
        userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER) && (
          <JudgementValidationButtons
            onValidation={() =>
              checkExpertiseOnValidation(expertReport, props.expertiseReport)
                ? setOpenDialogValidation(true)
                : handleValidateExpertReport()
            }
            onCancel={() =>
              !featureService.hasFeature(FEATURES.EVOLUTIONS_FEE_NOTE) &&
              pushTo(PAGES.COURT_MANAGER_ASSIGNED_CLAIM)
            }
            cancelMessage={messages.courtManagerJudgementDecisionCancel}
          />
        )}
      {featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER) && (
        <ValidationDialog
          open={openDialogValidation}
          handleClose={() => setOpenDialogValidation(false)}
          handleValidate={handleValidateExpertReport}
        />
      )}
      <RejectionDialog
        isRejectionDialogOpen={isRejectionDialogOpen}
        setIsRejectionDialogOpen={setIsRejectionDialogOpen}
        requestValidateExpertReport={props.requestValidateExpertReport}
        expertReport={expertReport}
        expertiseReport={props.expertiseReport}
        lawyer={props.lawyer}
        claimId={props.claimId}
        claimNumber={props.claimNumber}
        judiciaryProcessId={props.judiciaryProcessId}
        isAppeal
      />
    </Container>
  );
};

export default injectIntl(CourtManagerExpertReportAppeal);
